import { fontStyleToCss, type FontOptions } from '@/lib/fontStyle';
import { sanitizeHtml } from '@/lib/html';

/**
 * The four text primitives — `TextBloc`, `TitleBloc`, `MainTitle`, `SubTitle` — from one
 * implementation.
 *
 * The legacy app had them as four near-identical files (`textBloc.vue`, `titleBloc.vue`,
 * `mainTitle.vue`, `subtTitle.vue`) differing only in default font size, wrapper class,
 * padding and whether the text is escaped. Everything else, including the whole font block,
 * was copy-pasted four times.
 *
 * ## The props are unified
 *
 * Legacy named the props per component — `text`/`textFont`, `titleText`/`titleFont`,
 * `mainTitle`/`mainTitleFont`, `subTitleText`/`subTitleFont`. All four take `text` and `font`
 * here. Those names are internal (no config key is called `text` or `font`) and SYM-496 and
 * SYM-501 consume this API, so it is one shape rather than four.
 *
 * ## They render nothing when empty
 *
 * Every legacy template is wrapped in `v-if`, and several widgets rely on it: a title block
 * that rendered an empty `<div>` would still contribute its `padding-bottom` and push the
 * chart below it out of place.
 */

export type TextBlockProps = {
  /** Rendered as HTML (sanitised) except in `TitleBloc`. Empty or absent renders nothing. */
  text: string | undefined;
  /** `options.<name>Font` from config. Missing fields fall back per component. */
  font?: FontOptions;
  /**
   * Appended to the component's own classes. Exists for `FloorOccupancyWidget` (SYM-496),
   * which styles its `MainTitle` with an extra `.component-main-title` rule.
   */
  className?: string;
};

/** What distinguishes the four. Everything else in this file is shared. */
type TextBlockVariant = {
  /** The legacy class name first, then the utilities replacing its scoped SCSS. */
  className: string;
  /** docs/MIGRATION-WIDGETS.md §2, "defaults by site". */
  fontSize: string;
  /** `titleBloc` used `v-text`; the other three used `v-html`. */
  escape: boolean;
};

/**
 * `1rem` and `1.5rem` of `padding-bottom` are `pb-4` and `pb-6` on Tailwind 4's 0.25rem
 * spacing scale — not `pb-1`/`pb-1.5`. The legacy class names (`textBloc`, `title`,
 * `mainTitle`, `sub-title`) carry no CSS here and are kept as markers, so that a DOM diff
 * against the legacy app during SYM-506 lines up.
 */
const VARIANTS = {
  textBloc: { className: 'textBloc w-auto', fontSize: '1.2rem', escape: false },
  title: { className: 'title w-auto pb-4', fontSize: '2rem', escape: true },
  mainTitle: { className: 'mainTitle w-full text-center pb-6', fontSize: '2rem', escape: false },
  subTitle: { className: 'sub-title w-auto pb-4', fontSize: '2rem', escape: false },
} satisfies Record<string, TextBlockVariant>;

function TextBlock({ text, font, className, variant }: TextBlockProps & { variant: TextBlockVariant }) {
  // `v-if="text"`, verbatim: `undefined` and `''` both render nothing at all.
  if (!text) return null;

  const style = fontStyleToCss(font, { fontSize: variant.fontSize });
  const classes = className ? `${variant.className} ${className}` : variant.className;

  if (variant.escape) {
    return (
      <div className={classes} style={style}>
        {text}
      </div>
    );
  }

  // Raw HTML is a legacy behaviour these configs depend on (`<br>`, `<b>` inside body text),
  // and it is why `sanitizeHtml` exists — after `{{variable}}` substitution this string can be
  // part config and part broker payload, and the two are no longer distinguishable
  // (docs/MIGRATION-WIDGETS.md §4).
  return (
    <div className={classes} style={style} dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }} />
  );
}

/** Body text. 1.2rem, raw HTML. */
export function TextBloc(props: TextBlockProps) {
  return <TextBlock {...props} variant={VARIANTS.textBloc} />;
}

/** Section title. 2rem, **escaped** — the one primitive that does not render HTML. */
export function TitleBloc(props: TextBlockProps) {
  return <TextBlock {...props} variant={VARIANTS.title} />;
}

/** Full-width centred heading. 2rem, raw HTML. */
export function MainTitle(props: TextBlockProps) {
  return <TextBlock {...props} variant={VARIANTS.mainTitle} />;
}

/** Secondary title. 2rem, raw HTML. */
export function SubTitle(props: TextBlockProps) {
  return <TextBlock {...props} variant={VARIANTS.subTitle} />;
}
